import type { HeroKeywordsSortField } from "@/types";
import { Keyword, SimpleSourceTag, UtilizationTag } from "@/types/keyword";
import { compareNullableNumber } from "@/utils/keywordMetricDisplay";
import {
  RANGE_FILTER_COLUMNS,
  type RangeFilterState,
} from "@/components/keyword-bank/constants";
import { getVisibleUtilizationTags } from "./utils";

const RANGE_FILTER_FIELD_BY_ID: Record<string, string> = Object.fromEntries(
  RANGE_FILTER_COLUMNS.map((col) => [col.id, col.field]),
);

export type KeywordBankFilterState = {
  masterListOnly: boolean;
  search: string;
  utilization: string[];
  source: string[];
  theme: string[];
  rangeFilters?: RangeFilterState;
  // AUT-419: comma-separated term lists. `phrasesContaining` keeps a keyword that contains the terms
  // per `phrasesMode` (Any = at least one term, All = every term; default Any). `excludeKeywords`
  // drops a keyword if it contains ANY term.
  phrasesContaining?: string[];
  phrasesMode?: "any" | "all";
  excludeKeywords?: string[];
};

/** Split a raw comma-separated filter string into lowercased, non-empty terms. */
export function parseTermList(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t.length > 0);
}

function keywordMatchesSource(kw: Keyword, sources: string[]): boolean {
  if (sources.length === 0) return true;
  const tags = kw.sourceTags ?? [];
  return sources.some((s) => {
    if (s === SimpleSourceTag.LANDSCAPE) {
      return (
        tags.some((t) => typeof t === "object" && t.type === "Landscape") ||
        kw.landscape_id != null
      );
    }
    return tags.some((t) => t === s);
  });
}

function keywordMatchesUtilization(kw: Keyword, selected: string[]): boolean {
  if (selected.length === 0) return true;
  // Match against the same tags the Utilization column actually displays
  // (AUT-34) — "Landscaped" only matches the keyword that IS its landscape's
  // Priority Search Phrase, not every keyword merely pulled in by one.
  const visible = getVisibleUtilizationTags(kw);
  return selected.some((tag) => visible.includes(tag as UtilizationTag));
}

/**
 * AUT-496: a filter compares what the column shows, not what it stores.
 *
 * SERP Match is the only range column stored with a decimal (`NUMBER(10, 1)`) and rendered rounded,
 * so it is the only one where the two can disagree — and they did: "holbrook" displays 56% (56.3
 * stored), and Min 56 / Max 56 returned none of 117 keywords. Someone filtering for the number in
 * front of them got an empty table, which reads as a broken filter rather than a rounding rule
 * nobody can see (QA, 2026-10-30).
 *
 * Only this field is rounded. Every other range column already stores what it displays, and
 * rounding them here would change filters this ticket has no business touching.
 */
const ROUNDED_RANGE_FIELDS = new Set(["serp_match_pct"]);

function getRangeFieldValue(kw: Keyword, field: string): number | null {
  if (field === "search_volume_trend") {
    const v = kw.search_volume_trend;
    if (v == null || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  const val = (kw as unknown as Record<string, unknown>)[field];
  if (val == null) return null;
  const n = Number(val);
  if (!Number.isFinite(n)) return null;

  return ROUNDED_RANGE_FIELDS.has(field) ? Math.round(n) : n;
}

function keywordPassesRangeFilters(
  kw: Keyword,
  rangeFilters: RangeFilterState,
): boolean {
  for (const [id, bounds] of Object.entries(rangeFilters)) {
    if (bounds.min == null && bounds.max == null) continue;
    const field = RANGE_FILTER_FIELD_BY_ID[id] ?? id;
    const value = getRangeFieldValue(kw, field);
    if (value == null || !Number.isFinite(value)) return false;
    if (bounds.min != null && value < bounds.min) return false;
    if (bounds.max != null && value > bounds.max) return false;
  }
  return true;
}

/** Server-style filters + segment, applied client-side on the full keyword list. */
export function filterKeywordsForBank(
  keywords: Keyword[],
  opts: KeywordBankFilterState,
  keywordThemeNames?: Map<number, string>,
): Keyword[] {
  let list = keywords;
  if (opts.masterListOnly) {
    list = list.filter((k) => k.is_master_list);
  }
  const rawSearch = opts.search.trim();
  if (rawSearch) {
    const isExact =
      (rawSearch.startsWith('"') && rawSearch.endsWith('"')) ||
      (rawSearch.startsWith("'") && rawSearch.endsWith("'"));
    const q = isExact
      ? rawSearch.slice(1, -1).trim().toLowerCase()
      : rawSearch.toLowerCase();
    list = list.filter((k) =>
      isExact
        ? k.search.toLowerCase() === q
        : k.search.toLowerCase().includes(q),
    );
  }
  if (opts.phrasesContaining && opts.phrasesContaining.length > 0) {
    const all = opts.phrasesMode === "all";
    list = list.filter((k) => {
      const s = k.search.toLowerCase();
      return all
        ? opts.phrasesContaining!.every((t) => s.includes(t))
        : opts.phrasesContaining!.some((t) => s.includes(t));
    });
  }
  if (opts.excludeKeywords && opts.excludeKeywords.length > 0) {
    list = list.filter((k) => {
      const s = k.search.toLowerCase();
      return !opts.excludeKeywords!.some((t) => s.includes(t));
    });
  }
  if (opts.utilization.length > 0) {
    list = list.filter((k) => keywordMatchesUtilization(k, opts.utilization));
  }
  if (opts.source.length > 0) {
    list = list.filter((k) => keywordMatchesSource(k, opts.source));
  }
  if (opts.theme.length > 0 && keywordThemeNames) {
    list = list.filter((k) => {
      const name = keywordThemeNames.get(k.id);
      return opts.theme.some((t) =>
        t === "__unthemed__" ? name == null : name === t,
      );
    });
  }
  if (opts.rangeFilters) {
    list = list.filter((k) => keywordPassesRangeFilters(k, opts.rangeFilters!));
  }
  return list;
}

function trendNumber(kw: Keyword): number | null {
  const v = kw.search_volume_trend;
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function sortHeroKeywords(
  keywords: Keyword[],
  field: HeroKeywordsSortField,
  direction: "asc" | "desc",
): Keyword[] {
  const mul = direction === "asc" ? 1 : -1;
  const cmpId = (a: Keyword, b: Keyword) => a.id - b.id;

  const withIndex = keywords?.map((k) => ({ k }));
  withIndex.sort((a, b) => {
    const A = a.k;
    const B = b.k;
    let c = 0;
    switch (field) {
      case "keyword":
        c = A.search.localeCompare(B.search) * mul;
        break;
      case "heroRank":
        c = compareNullableNumber(A.hero_rank, B.hero_rank, direction);
        break;
      case "exactVolume":
        c = compareNullableNumber(A.search_volume, B.search_volume, direction);
        break;
      case "kpg":
        c = compareNullableNumber(A.kps, B.kps, direction);
        break;
      case "searchVolumeTrend":
        c = compareNullableNumber(trendNumber(A), trendNumber(B), direction);
        break;
      case "ppcMaxBid":
        c = compareNullableNumber(A.ppc_max_bid, B.ppc_max_bid, direction);
        break;
      case "heroRelevanceScore":
        c = compareNullableNumber(
          A.hero_relevance,
          B.hero_relevance,
          direction,
        );
        break;
      // AUT-496. Sorts on the percentage alone: a low-sample 90% outranks a solid 80%, which is
      // what the user asked for by sorting. The thin ones are marked in the cell, not reordered.
      case "serpMatch":
        c = compareNullableNumber(
          A.serp_match_pct,
          B.serp_match_pct,
          direction,
        );
        break;
      case "keywordSales":
        c = compareNullableNumber(A.keyword_sales, B.keyword_sales, direction);
        break;
      case "asinDensity":
        c = compareNullableNumber(A.asin_density, B.asin_density, direction);
        break;
      case "competition":
        c = compareNullableNumber(A.competitors, B.competitors, direction);
        break;
      default:
        c = 0;
    }
    if (c !== 0) return c;
    return cmpId(A, B) * mul;
  });
  return withIndex?.map(({ k }) => k) ?? [];
}
